import { defineCopy } from '../pageCopy';

/**
 * Copy for the /products index (en, ar, ckb). Product names (DilaCRM, DilaERP,
 * dila.menu) stay Latin everywhere. Structural fields (slug, accent, image,
 * domain) still come from `src/lib/products.ts`; the English page keeps
 * reading its product text from there, ar/ckb read from `items` below.
 */
const en = {
  meta: {
    title: 'Products',
    description:
      'Software Datacode builds, runs and sells: DilaCRM, the AI inbox for every customer channel; DilaERP, the ERP that speaks Kurdish; and dila.menu, digital menus and restaurant management for Kurdistan.',
    ogDescription:
      'DilaCRM, DilaERP and dila.menu. Built in Erbil, running in restaurants, offices and shops across Kurdistan and beyond.',
    twitterDescription: 'DilaCRM, DilaERP and dila.menu: software Datacode builds, runs and sells.',
  },
  crumbHome: 'Home',
  hero: {
    title: 'software we stake our name on',
    sub: 'DilaCRM, DilaERP and dila.menu. Built in Erbil, running in restaurants, offices and shops across Kurdistan and beyond.',
    bookDemo: 'Book a demo',
    lineup: 'Meet the lineup',
  },
  highlights: 'Highlights',
  cursorView: 'View',
  items: {
    dilacrm: {
      tagline: 'The AI inbox that answers before your competitor wakes up.',
      blurb:
        'WhatsApp, Instagram, Messenger, Telegram, TikTok and email in one inbox. AI drafts the reply in Kurdish, Arabic or English, you approve, it sends.',
      chips: ['6 channels', '5 languages', '24/7 AI'],
      imageAlt:
        'DilaCRM inbox showing an AI-suggested reply awaiting human approval, with Send, Edit and Don’t send actions.',
      explore: 'Explore DilaCRM',
    },
    dilaerp: {
      tagline: 'The ERP that speaks Kurdish.',
      blurb:
        'Sales, stock, accounting, HR, payroll and POS in one simple system. Fifteen modules, three languages, self-hosted so your data stays yours.',
      chips: ['15 modules', '300 screens', '3 languages'],
      imageAlt:
        'DilaERP dashboard with monthly revenue, money owed, overdue invoices and a 30-day revenue chart in IQD.',
      explore: 'Explore DilaERP',
    },
    dilamenu: {
      tagline: 'Digital menus, beautifully served.',
      blurb:
        'QR menus guests open in two seconds, WhatsApp and dine-in ordering that reaches your kitchen, and dila RMS, the POS built for Iraqi hospitality.',
      chips: ['38 venues', '11 themes', '<2s load'],
      imageAlt:
        'A dila.menu restaurant menu in the Kilim theme showing a chef’s signature Adana Kebab with IQD pricing and food photography.',
      explore: 'Explore dila.menu',
    },
  },
  crm: {
    chipEmail: 'TikTok · Telegram · Email',
  },
  erp: {
    bullets: [
      'Fifteen modules from invoicing to payroll, behind one login.',
      'An AI assistant that answers in Kurdish, Arabic and English.',
      'Self-hosted. Your data stays yours.',
    ],
    chipProfit: 'Profit & Loss',
    chipPayroll: 'Payroll',
    chipPos: 'POS · Stock · CRM',
  },
  menu: {
    welcomeAlt: 'dila.menu welcome screen offering English, Arabic and Kurdish.',
    modalAlt: 'dila.menu item detail showing weight, calories, origin and ingredients.',
  },
  why: {
    kicker: 'Why ours is different',
    title: 'built here, proven here',
    lede: 'Three convictions every Datacode product shares.',
    rows: [
      {
        title: 'Kurdish first, not translated last',
        body: 'Interfaces, AI replies, receipts and PDFs ship in Sorani, Arabic and English with proper right-to-left. Localization is the foundation, not a patch.',
      },
      {
        title: 'We run what we sell',
        body: 'Our own leads are answered by DilaCRM, and real restaurants close their nights on dila RMS. When something breaks, it breaks for us first.',
      },
      {
        title: 'You keep your data',
        body: 'Self-hosted options, honest exports, no hostage pricing. Your business stays yours, down to the last row.',
      },
    ],
  },
  demo: {
    ghost: 'products',
    eyebrow: 'Book a demo',
    title: 'see them running, then book a demo',
    sub: 'Twenty minutes on a call, or walk into any restaurant on dila.menu and scan the QR on the table. The software is already out there working.',
    cta: 'Book a demo',
  },
};

const ar: typeof en = {
  meta: {
    title: 'المنتجات',
    description:
      'برمجيات تبنيها Datacode وتشغّلها وتبيعها: DilaCRM، صندوق الوارد الذكي لكل قنوات الزبائن؛ وDilaERP، نظام ERP الذي يتكلم الكردية؛ وdila.menu، القوائم الرقمية وإدارة المطاعم في كردستان.',
    ogDescription:
      'DilaCRM وDilaERP وdila.menu. بُنيت في أربيل، وتعمل في مطاعم ومكاتب ومحلات عبر كردستان وخارجها.',
    twitterDescription: 'DilaCRM وDilaERP وdila.menu: برمجيات تبنيها Datacode وتشغّلها وتبيعها.',
  },
  crumbHome: 'الرئيسية',
  hero: {
    title: 'برمجيات نضع اسمنا عليها',
    sub: 'DilaCRM وDilaERP وdila.menu. بُنيت في أربيل، وتعمل اليوم في مطاعم ومكاتب ومحلات عبر كردستان وخارجها.',
    bookDemo: 'احجز عرضاً تجريبياً',
    lineup: 'تعرّف على منتجاتنا',
  },
  highlights: 'أبرز النقاط',
  cursorView: 'عرض',
  items: {
    dilacrm: {
      tagline: 'صندوق الوارد الذكي الذي يجاوب قبل أن يصحى منافسك.',
      blurb:
        'WhatsApp وInstagram وMessenger وTelegram وTikTok والبريد الإلكتروني في صندوق واحد. الذكاء الاصطناعي يكتب مسودة الرد بالكردية أو العربية أو الإنجليزية، وأنت توافق، فيُرسل.',
      chips: ['6 قنوات', '5 لغات', 'ذكاء اصطناعي 24/7'],
      imageAlt:
        'صندوق وارد DilaCRM يعرض رداً مقترحاً من الذكاء الاصطناعي بانتظار موافقة بشرية، مع أزرار الإرسال والتعديل وعدم الإرسال.',
      explore: 'استكشف DilaCRM',
    },
    dilaerp: {
      tagline: 'نظام ERP الذي يتكلم الكردية.',
      blurb:
        'المبيعات والمخزون والمحاسبة والموارد البشرية والرواتب ونقاط البيع في نظام واحد بسيط. خمس عشرة وحدة، وثلاث لغات، واستضافة ذاتية حتى تبقى بياناتك ملكك.',
      chips: ['15 وحدة', '300 شاشة', '3 لغات'],
      imageAlt:
        'لوحة DilaERP تعرض الإيرادات الشهرية والمبالغ المستحقة والفواتير المتأخرة ومخطط إيرادات 30 يوماً بالدينار العراقي.',
      explore: 'استكشف DilaERP',
    },
    dilamenu: {
      tagline: 'قوائم رقمية، تُقدَّم بأجمل صورة.',
      blurb:
        'قوائم QR يفتحها الضيوف خلال ثانيتين، وطلبات WhatsApp والطلب داخل المطعم التي تصل إلى مطبخك، وdila RMS، نظام نقاط البيع المصمم للضيافة العراقية.',
      chips: ['38 منشأة', '11 قالباً', 'تحميل أقل من ثانيتين'],
      imageAlt:
        'قائمة مطعم على dila.menu بقالب Kilim تعرض طبق أضنة كباب المميز للشيف مع الأسعار بالدينار العراقي وصور الطعام.',
      explore: 'استكشف dila.menu',
    },
  },
  crm: {
    chipEmail: 'TikTok · Telegram · بريد إلكتروني',
  },
  erp: {
    bullets: [
      'خمس عشرة وحدة من الفوترة إلى الرواتب، خلف تسجيل دخول واحد.',
      'مساعد ذكاء اصطناعي يجاوب بالكردية والعربية والإنجليزية.',
      'استضافة ذاتية. بياناتك تبقى ملكك.',
    ],
    chipProfit: 'الأرباح والخسائر',
    chipPayroll: 'الرواتب',
    chipPos: 'POS · المخزون · CRM',
  },
  menu: {
    welcomeAlt: 'شاشة الترحيب في dila.menu بثلاث لغات: الإنجليزية والعربية والكردية.',
    modalAlt: 'تفاصيل صنف في dila.menu تعرض الوزن والسعرات والمنشأ والمكونات.',
  },
  why: {
    kicker: 'ما الذي يميّزنا',
    title: 'بُنيت هنا، وأُثبتت هنا',
    lede: 'ثلاث قناعات تشترك فيها كل منتجات Datacode.',
    rows: [
      {
        title: 'الكردية أولاً، وليست ترجمة في الآخر',
        body: 'الواجهات وردود الذكاء الاصطناعي والإيصالات وملفات PDF تصدر بالسورانية والعربية والإنجليزية مع دعم صحيح للكتابة من اليمين إلى اليسار. التوطين هو الأساس، وليس ترقيعة.',
      },
      {
        title: 'نشغّل ما نبيعه',
        body: 'عملاؤنا المحتملون يُجاب عنهم عبر DilaCRM، ومطاعم حقيقية تُقفل لياليها على dila RMS. وحين يتعطل شيء، يتعطل عندنا أولاً.',
      },
      {
        title: 'بياناتك تبقى لك',
        body: 'خيارات استضافة ذاتية، وتصدير صادق للبيانات، وبلا تسعير يحتجزك رهينة. عملك يبقى ملكك حتى آخر سطر.',
      },
    ],
  },
  demo: {
    ghost: 'المنتجات',
    eyebrow: 'احجز عرضاً تجريبياً',
    title: 'شاهدها وهي تعمل، ثم احجز عرضاً تجريبياً',
    sub: 'عشرون دقيقة في مكالمة، أو ادخل أي مطعم على dila.menu وامسح رمز QR على الطاولة. البرنامج يعمل هناك فعلاً.',
    cta: 'احجز عرضاً تجريبياً',
  },
};

const ckb: typeof en = {
  meta: {
    title: 'بەرهەمەکان',
    description:
      'نەرمەکاڵایەک کە Datacode دروستی دەکات، بەڕێوەی دەبات و دەیفرۆشێت: DilaCRM، سندوقی نامەی زیرەک بۆ هەموو کەناڵەکانی کڕیار؛ DilaERP، ERPێک کە بە کوردی قسە دەکات؛ و dila.menu، مینیوی دیجیتاڵ و بەڕێوەبردنی ڕێستۆرانت بۆ کوردستان.',
    ogDescription:
      'DilaCRM و DilaERP و dila.menu. لە هەولێر دروستکراون و لە ڕێستۆرانت و ئۆفیس و دوکانەکانی کوردستان و دەرەوەی کوردستان کار دەکەن.',
    twitterDescription:
      'DilaCRM و DilaERP و dila.menu: نەرمەکاڵایەک کە Datacode دروستی دەکات، بەڕێوەی دەبات و دەیفرۆشێت.',
  },
  crumbHome: 'سەرەکی',
  hero: {
    title: 'نەرمەکاڵایەک کە ناوی خۆمانی لەسەر دادەنێین',
    sub: 'DilaCRM و DilaERP و dila.menu. لە هەولێر دروستکراون و ئێستا لە ڕێستۆرانت و ئۆفیس و دوکانەکانی کوردستان و دەرەوەی کوردستان کار دەکەن.',
    bookDemo: 'داوای دیمۆ بکە',
    lineup: 'بەرهەمەکانمان بناسە',
  },
  highlights: 'تایبەتمەندییە سەرەکییەکان',
  cursorView: 'بینین',
  items: {
    dilacrm: {
      tagline: 'سندوقی نامەی زیرەک کە پێش ئەوەی ڕکابەرەکەت لە خەو هەستێت وەڵام دەداتەوە.',
      blurb:
        'WhatsApp و Instagram و Messenger و Telegram و TikTok و ئیمەیل لە یەک سندوقدا. زیرەکی دەستکرد ڕەشنووسی وەڵام بە کوردی، عەرەبی یان ئینگلیزی دەنووسێت، تۆ پەسەندی دەکەیت، دەنێردرێت.',
      chips: ['6 کەناڵ', '5 زمان', 'زیرەکی دەستکرد 24/7'],
      imageAlt:
        'سندوقی نامەی DilaCRM کە وەڵامێکی پێشنیارکراوی زیرەکی دەستکرد پیشان دەدات و چاوەڕێی پەسەندکردنی مرۆڤە، لەگەڵ دوگمەی ناردن و دەستکاریکردن و نەناردن.',
      explore: 'DilaCRM بناسە',
    },
    dilaerp: {
      tagline: 'ERPێک کە بە کوردی قسە دەکات.',
      blurb:
        'فرۆش، کۆگا، ژمێریاری، سەرچاوە مرۆییەکان، مووچە و POS لە یەک سیستەمی سادەدا. پازدە مۆدیوڵ، سێ زمان، لەسەر سێرڤەری خۆت دادەمەزرێت بۆ ئەوەی داتاکانت هی خۆت بمێننەوە.',
      chips: ['15 مۆدیوڵ', '300 شاشە', '3 زمان'],
      imageAlt:
        'داشبۆردی DilaERP کە داهاتی مانگانە، ئەو پارەیەی قەرزارن، فاتورە دواکەوتووەکان و هێڵکاری داهاتی 30 ڕۆژ بە دیناری عێراقی پیشان دەدات.',
      explore: 'DilaERP بناسە',
    },
    dilamenu: {
      tagline: 'مینیوی دیجیتاڵ، بە جوانترین شێوە پێشکەش دەکرێت.',
      blurb:
        'مینیوی QR کە میوان لە دوو چرکەدا دەیکاتەوە، داواکاری WhatsApp و ناو ڕێستۆرانت کە دەگاتە چێشتخانەکەت، و dila RMS، سیستەمی POSی دروستکراو بۆ میوانداری عێراقی.',
      chips: ['38 شوێن', '11 ڕووکار', 'کەمتر لە 2 چرکە بار دەبێت'],
      imageAlt:
        'مینیوی ڕێستۆرانتێک لەسەر dila.menu بە ڕووکاری Kilim کە ئەدەنە کەبابی تایبەتی سەرچێشتلێنەر بە نرخی دینار و وێنەی خواردنەوە پیشان دەدات.',
      explore: 'dila.menu بناسە',
    },
  },
  crm: {
    chipEmail: 'TikTok · Telegram · ئیمەیل',
  },
  erp: {
    bullets: [
      'پازدە مۆدیوڵ لە فاتورەوە تا مووچە، لەپشت یەک چوونەژوورەوە.',
      'یاریدەدەرێکی زیرەکی دەستکرد کە بە کوردی و عەرەبی و ئینگلیزی وەڵام دەداتەوە.',
      'لەسەر سێرڤەری خۆت. داتاکانت هی خۆتن.',
    ],
    chipProfit: 'قازانج و زیان',
    chipPayroll: 'مووچە',
    chipPos: 'POS · کۆگا · CRM',
  },
  menu: {
    welcomeAlt: 'شاشەی بەخێرهاتنی dila.menu کە زمانی ئینگلیزی و عەرەبی و کوردی پێشکەش دەکات.',
    modalAlt: 'وردەکاری خواردنێک لە dila.menu کە کێش و کالۆری و ڕەچەڵەک و پێکهاتەکان پیشان دەدات.',
  },
  why: {
    kicker: 'بۆچی جیاوازین',
    title: 'لێرە دروستکراوە، لێرە سەلمێنراوە',
    lede: 'سێ باوەڕ کە هەموو بەرهەمەکانی Datacode هاوبەشیانن.',
    rows: [
      {
        title: 'کوردی یەکەم، نەک وەرگێڕان لە کۆتاییدا',
        body: 'ڕووکار و وەڵامی زیرەکی دەستکرد و وەسڵ و PDF بە سۆرانی و عەرەبی و ئینگلیزی دەردەچن، بە پشتگیرییەکی دروستی نووسین لە ڕاستەوە بۆ چەپ. خۆجێیکردن بناغەیە، نەک پێچاندنەوەیەکی دواتر.',
      },
      {
        title: 'ئەوەی دەیفرۆشین خۆمان بەکاری دەهێنین',
        body: 'لیدەکانی خۆمان بە DilaCRM وەڵام دەدرێنەوە و ڕێستۆرانتی ڕاستەقینە شەوەکانیان بە dila RMS دادەخەن. کاتێک شتێک تێک دەچێت، یەکەم جار بۆ ئێمە تێک دەچێت.',
      },
      {
        title: 'داتاکانت هی خۆتن',
        body: 'هەڵبژاردنی سێرڤەری خۆت، ئێکسپۆرتی ڕاستگۆیانە، بێ نرخێک کە بتکاتە بارمتە. بازرگانییەکەت هی خۆتە تا دوایین ڕیز.',
      },
    ],
  },
  demo: {
    ghost: 'بەرهەمەکان',
    eyebrow: 'داوای دیمۆ بکە',
    title: 'بیانبینە کار دەکەن، پاشان دیمۆیەک داوا بکە',
    sub: 'بیست خولەک لە پەیوەندییەکدا، یان بچۆ ناو هەر ڕێستۆرانتێکی dila.menu و QRی سەر مێزەکە سکان بکە. نەرمەکاڵاکە پێشتر لە دەرەوە کار دەکات.',
    cta: 'داوای دیمۆ بکە',
  },
};

export const productsIndexCopy = defineCopy(en, ar, ckb);
